import { useGet, usePost } from "@/apiHooks/HttpFunctions";
import {
  citiesApi,
  countriesApi,
  teamsMasterDataApi,
  sportsMasterDataApi,
  teamGroupsMasterDataApi,
} from "@/apis/apis";
import AsyncDropdown from "@/components/AsyncDropdown/AsyncDropdown";
import BasicInput from "@/components/BasicInput";
import Button from "@/components/Button";
import ImageInput from "@/components/ImageInput/Index";
import PageHeaderName from "@/components/PageHeaderName/PageHeaderName";
import RenderToast from "@/components/RenderToast";
import RichTextEditor from "@/components/RichTextEditor";
import Spinner from "@/components/Spinner";
import { blogPostHistoryActionLabels } from "@/constant/Blogs/Posts/history-action";
import { LOADING_PLACEHOLDER } from "@/constant/constants";
import { HTTP_METHODS } from "@/constant/Http/methods";
import { Active, Deactivated } from "@/constant/imagePath";
import {
  DEFAULT_LANGUAGE,
  DEFAULT_LANGUAGE_LABEL,
} from "@/constant/Languages/language";
import { LIST_MODE, LIST_PER_PAGE } from "@/constant/Lists/listMode";
import {
  STATUS,
  statusBgColors,
  statusLabels,
} from "@/constant/statusConstants";
import { useLayout } from "@/context/LayoutContext";
import { useModalConfirmation } from "@/context/ModalConfirmationContext";
import { useModalInfo } from "@/context/ModalContext";
import useModalGetOptions from "@/CustomHooks/Modal/useModalGetOptions";
import useFormikErrorToast from "@/CustomHooks/useFormikErrorToast";
import useHttpErrorToast from "@/CustomHooks/useHttpErrorToast";
import useUnsavedChangesWarning from "@/hooks/useUnsavedChangesWarning";
import TeamInitialValues from "@/data/WebDataManagement/MasterData/Teams/initial-values";
import TeamSchema from "@/schema/WebDataManagement/MasterData/Teams/TeamSchema";
import { cn } from "@/utils/cn";
import { formatDateTime } from "@/utils/helper";
import { Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useFormik } from "formik";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import RadioInput from "@/components/RadioInput/Index";
import CreatableDropdown from "@/components/Creatable/Creatable";
import { booleanOptionFormOptions } from "@/constant/GeneralOptions/boolean";
import StatusInput from "@/components/StatusInput";

export default function TeamsFormPage() {
  // Path Params
  const { id } = useParams();
  const isEdit = !!id;

  // Hooks
  const locales = useSelector(
    (state) => state?.siteConfigurationReducer?.locales
  );

  const localeCodes = useMemo(
    () => locales?.map((locale) => locale.code) ?? [],
    [locales]
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const targetLang = searchParams.get("target_lang") || DEFAULT_LANGUAGE;
  const { setIsFullPage } = useLayout();
  const { openModalConfirmation } = useModalConfirmation();
  const { openModalInfo } = useModalInfo();
  const navigate = useNavigate();
  const initCurrentCount = { char: 0, word: 0 };
  const [currentCount, setCurrentCount] = useState(initCurrentCount);
  const showApi = isEdit ? `${teamsMasterDataApi}/${id}` : null;
  const {
    data: showData,
    isFetching: showIsFetching,
    error: showError,
  } = useGet({
    apiURL: showApi,
    params: { target_lang: targetLang },
    enabled: isEdit,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
  });
  const {
    data: showMetaData,
    isFetching: showMetaIsFetching,
    error: showMetaError,
  } = useGet({
    apiURL: `${showApi}/meta`,
    enabled: isEdit,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
  });

  // Formik
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      ...TeamInitialValues(
        targetLang,
        localeCodes,
        !showIsFetching ? showData : null
      ),
      tags:
        !showIsFetching && showData?.data?.tags
          ? showData.data.tags.map((tag) => ({
              value: tag.name,
              label: tag.name,
            }))
          : [],
    },
    validationSchema: TeamSchema(localeCodes, isEdit, targetLang),
    onSubmit: (values) => handleSubmit(values),
  });

  useUnsavedChangesWarning(formik.dirty);
  const statusLabelsMap = statusLabels();
  const statusRadioOptions = [
    {
      value: STATUS.ACTIVE,
      label: statusLabelsMap[STATUS.ACTIVE],
      imageSrc: Active,
    },
    {
      value: STATUS.INACTIVE,
      label: statusLabelsMap[STATUS.INACTIVE],
      imageSrc: Deactivated,
    },
  ];

  // Update the current counts
  const updateCounts = useCallback((text) => {
    setCurrentCount({
      char: countChar(text),
      word: countWord(text),
    });
  }, []);

  // useEffects
  useEffect(() => {
    if (showError?.status === 404) {
      RenderToast({
        type: "error",
        message: "Team Profile translation not found.",
      });
    }
    if (showMetaError?.status === 404) {
      RenderToast({
        type: "error",
        message: "Failed to get all of the Team Profile translation status.",
      });
    }
  }, [showError, showMetaError]);
  useEffect(() => {
    if (targetLang) {
      const params = new URLSearchParams();
      params.set("target_lang", targetLang);
      setSearchParams(params);
    }
  }, [targetLang, setSearchParams]);
  useEffect(() => {
    setIsFullPage(false);
    return () => setIsFullPage(true);
  }, [setIsFullPage]);

  // Functions
  const triggerUnsavedChangesWarning = useUnsavedChangesWarning(formik.dirty);
  const handleLanguageTab = (event, localeCode) => {
    if (!triggerUnsavedChangesWarning()) {
      event.preventDefault();
      return;
    }

    formik.resetForm();

    setSearchParams((prevParams) => {
      const newParams = new URLSearchParams(prevParams);
      newParams.set("target_lang", localeCode);
      return newParams;
    });
  };
  const getTextFromHtml = (html) => {
    const editor = new Editor({
      extensions: [StarterKit],
      content: html,
    });
    return editor.getText();
  };
  const handleSubmit = (values) => {
    const formData = new FormData();

    if (isEdit) formData.append("_method", "PUT");
    if (targetLang === DEFAULT_LANGUAGE) {
      formData.append(
        `tb_translations[${DEFAULT_LANGUAGE}][tagline]`,
        values.tagline
      );
      formData.append(
        `tb_translations[${DEFAULT_LANGUAGE}][content]`,
        values.content
      );
      formData.append(
        `tb_translations[${DEFAULT_LANGUAGE}][history_status]`,
        values.history_status
      );
      formData.append(`name[${targetLang}]`, values.name);
      formData.append("code", values.code);
      formData.append("status", values.status);
      formData.append("hexcode_1", values.hexcode_1);
      formData.append("hexcode_2", values.hexcode_2);
      formData.append("hexcode_3", values.hexcode_3);
      formData.append("hexcode_4", values.hexcode_4);
      formData.append("is_icon_bordered", values.is_icon_bordered);
      formData.append("is_national_team", values.is_national_team);
      formData.append("show_in_profile", values.show_in_profile);
      formData.append("city", values.city);
      formData.append("country_id", values.country_id);
      formData.append("foundation_year", values.foundation_year);
      formData.append("stadium_name", values.stadium_name);
      formData.append("stadium_capacity", values.stadium_capacity);
      formData.append("field_width", values.field_width);
      formData.append("field_length", values.field_length);
      formData.append("anthem_name", values.anthem_name);
      formData.append("mascot_1", values.mascot_1);
      formData.append("mascot_2", values.mascot_2);
      formData.append("rival_team_id_1", values.rival_team_id_1);
      formData.append("rival_team_id_2", values.rival_team_id_2);
      formData.append("rival_team_id_3", values.rival_team_id_3);
      formData.append("auto_translate", values.auto_translate);

      formData.append("sport_id", values.sport_id);

      if (values.team_group_id) {
        formData.append("team_group_id", values.team_group_id);
      }

      if (Array.isArray(values.tags)) {
        values.tags.forEach((tag) => {
          formData.append(`tags[]`, tag.value);
        });
      } else {
        formData.append(`tags[]`, null);
      }

      if (values.thumbnail_url && values.thumbnail_url instanceof File)
        formData.append("thumbnail_url", values.thumbnail_url);
      if (values.icon_url && values.icon_url instanceof File)
        formData.append("icon_url", values.icon_url);
      if (values.banner_url && values.banner_url instanceof File)
        formData.append("banner_url", values.banner_url);

      save(formData);

      return;
    } else {
      formData.append("_method", "PATCH");
      formData.append("target_lang", targetLang);
      formData.append(`name[${targetLang}]`, values.name);
      formData.append("tagline", values.tagline);
      formData.append("content", values.content);
      formData.append("history_status", values.history_status);
    }

    saveTranslation(formData);
  };

  const handleTranslate = () => {
    openModalConfirmation({
      title: "It will overwrite any existing text.",
      customMutation: {
        confirmationMessage: "Are you sure you want to translate the text?",
        apiURL: `${teamsMasterDataApi}/translate/${id}?target_lang=${targetLang}`,
        action: HTTP_METHODS.PUT,
        onSuccess: () => {
          openModalInfo("Team Profile translated successfully.");
        },
        onError: (error) => {
          RenderToast({
            type: "error",
            message:
              error.response.data.errors.error ?? "Something went wrong.",
          });
        },
      },
    });
  };
  const booleanOptionFormOptionsMap = booleanOptionFormOptions();

  const handleSuccess = (action) => (data) => {
    RenderToast({
      type: "success",
      message: `Team Profile ${action} successfully.`,
    });
    if (!isEdit) navigate("/web-data-management/master-data/teams");
  };
  const countChar = (text = "") => text.replace(/[\r\n]/g, "").length;
  const countWord = (text = "") =>
    text.trim().split(/\s+/).filter(Boolean).length;

  // Init the current content counts
  useEffect(() => {
    const content = getTextFromHtml(showData?.data?.content);
    updateCounts(content);
  }, [showData?.data?.content, updateCounts]);

  // Save form
  const {
    mutate: save,
    isPending: saveIsPending,
    error: saveError,
  } = usePost({
    isFormData: true,
    apiURL: isEdit ? `${teamsMasterDataApi}/${id}` : teamsMasterDataApi,
    onSuccess: handleSuccess(isEdit ? "edited" : "added"),
  });
  const {
    mutate: saveTranslation,
    isPending: saveTranslationIsPending,
    error: saveTranslationError,
  } = usePost({
    isFormData: true,
    apiURL: `${teamsMasterDataApi}/translate-overwritten/${id}`,
    onSuccess: handleSuccess("edited"),
  });

  // Get dropdown data
  const baseQueryParams = `?mode=${LIST_MODE.SELECT}&perPage=${LIST_PER_PAGE}&filter[keyword]=`;

  const countryListApi = `${countriesApi}${baseQueryParams}`;
  const cityListApi = `${citiesApi}${baseQueryParams}`;
  const teamGoupListApi = `${teamGroupsMasterDataApi}${baseQueryParams}`;
  const teamListApi = `${teamsMasterDataApi}${baseQueryParams}`;

  const sportListApi = `${sportsMasterDataApi}${baseQueryParams}`;
  const {
    loading: sportLoading,
    defaultOptions: sportDefaultOptions,
    debouncedFetchOptions: sportFetchOptions,
    getSelectedOption: sportGetSelectedOption,
  } = useModalGetOptions({
    modalState: {
      isOpen: targetLang === DEFAULT_LANGUAGE,
    },
    listApi: sportListApi,
    formikValue: formik.values.sport_id,
    formikInitialValue: formik.initialValues.sport_id,
    showData:
      isEdit && targetLang === DEFAULT_LANGUAGE && showData?.data?.sport,
  });

  const {
    loading: countryLoading,
    defaultOptions: countryDefaultOptions,
    debouncedFetchOptions: countryFetchOptions,
    getSelectedOption: countryGetSelectedOption,
  } = useModalGetOptions({
    modalState: {
      isOpen: targetLang === DEFAULT_LANGUAGE,
    },
    listApi: countryListApi,
    formikValue: formik.values.country_id,
    formikInitialValue: formik.initialValues.country_id,
    showData:
      isEdit && targetLang === DEFAULT_LANGUAGE && showData?.data?.country,
  });

  const {
    loading: teamGroupLoading,
    defaultOptions: teamGroupDefaultOptions,
    debouncedFetchOptions: teamGroupFetchOptions,
    getSelectedOption: teamGroupGetSelectedOption,
  } = useModalGetOptions({
    modalState: {
      isOpen: targetLang === DEFAULT_LANGUAGE,
    },
    listApi: teamGoupListApi,
    formikValue: formik.values.team_group_id,
    formikInitialValue: formik.initialValues.team_group_id,
    showData:
      isEdit && targetLang === DEFAULT_LANGUAGE && showData?.data?.team_group,
  });

  const {
    loading: cityLoading,
    defaultOptions: cityDefaultOptions,
    debouncedFetchOptions: cityFetchOptions,
    getSelectedOption: cityGetSelectedOption,
  } = useModalGetOptions({
    modalState: {
      isOpen: targetLang === DEFAULT_LANGUAGE,
    },
    listApi: cityListApi,
    formikValue: formik.values.city,
    formikInitialValue: formik.initialValues.city,
    showData:
      isEdit && targetLang === DEFAULT_LANGUAGE && showData?.data?.city_name,
  });

  const {
    loading: teamLoading1,
    defaultOptions: teamDefaultOptions1,
    debouncedFetchOptions: teamFetchOptions1,
    getSelectedOption: teamGetSelectedOption1,
  } = useModalGetOptions({
    modalState: {
      isOpen: targetLang === DEFAULT_LANGUAGE,
    },
    listApi: teamListApi,
    formikValue: formik.values.rival_team_id_1,
    formikInitialValue: formik.initialValues.rival_team_id_1,
    showData:
      isEdit && targetLang === DEFAULT_LANGUAGE && showData?.data?.rival_1,
  });

  const {
    loading: teamLoading2,
    defaultOptions: teamDefaultOptions2,
    debouncedFetchOptions: teamFetchOptions2,
    getSelectedOption: teamGetSelectedOption2,
  } = useModalGetOptions({
    modalState: {
      isOpen: targetLang === DEFAULT_LANGUAGE,
    },
    listApi: teamListApi,
    formikValue: formik.values.rival_team_id_2,
    formikInitialValue: formik.initialValues.rival_team_id_2,
    showData:
      isEdit && targetLang === DEFAULT_LANGUAGE && showData?.data?.rival_2,
  });

  const {
    loading: teamLoading3,
    defaultOptions: teamDefaultOptions3,
    debouncedFetchOptions: teamFetchOptions3,
    getSelectedOption: teamGetSelectedOption3,
  } = useModalGetOptions({
    modalState: {
      isOpen: targetLang === DEFAULT_LANGUAGE,
    },
    listApi: teamListApi,
    formikValue: formik.values.rival_team_id_3,
    formikInitialValue: formik.initialValues.rival_team_id_3,
    showData:
      isEdit && targetLang === DEFAULT_LANGUAGE && showData?.data?.rival_3,
  });

  const teamOptions = [
    {
      label: "Rival Team 1",
      fieldName: "rival_team_id_1",
      defaultValue: formik.values.rival_team_id_1,
      value: teamGetSelectedOption1(),
      showData:
        isEdit && targetLang === DEFAULT_LANGUAGE && showData?.data?.rival_1,
      options: teamDefaultOptions1,
      loadOptions: teamFetchOptions1,
      isLoading: teamLoading1,
    },
    {
      label: "Rival Team 2",
      fieldName: "rival_team_id_2",
      defaultValue: formik.values.rival_team_id_2,
      value: teamGetSelectedOption2(),
      showData:
        isEdit && targetLang === DEFAULT_LANGUAGE && showData?.data?.rival_2,
      options: teamDefaultOptions2,
      loadOptions: teamFetchOptions2,
      isLoading: teamLoading2,
    },
    {
      label: "Rival Team 3",
      fieldName: "rival_team_id_3",
      defaultValue: formik.values.rival_team_id_3,
      value: teamGetSelectedOption3(),
      showData:
        isEdit && targetLang === DEFAULT_LANGUAGE && showData?.data?.rival_3,
      options: teamDefaultOptions3,
      loadOptions: teamFetchOptions3,
      isLoading: teamLoading3,
    },
  ];

  const teamRivalInputs = teamOptions.map((option) => (
    <>
      <Row>
        <Col md={6}>
          <AsyncDropdown
            required={false}
            key={option.fieldName}
            name={option.fieldName}
            placeholder="No team selected. Type to search..."
            labelClass="mb-0 flex items-center"
            className="mb-4"
            containerClassName="!grid-cols-6 !gap-0"
            dropdownWrapperClassName="!col-span-5 !ml-0"
            label={option.label}
            defaultOptions={option.options}
            isLoading={option.isLoading}
            loadOptions={option.loadOptions}
            value={option.value}
            onChange={(selected) =>
              formik.setFieldValue(
                option.fieldName,
                selected?.value || formik.values[option.fieldName] || null
              )
            }
            errorText={
              (formik.touched[option.fieldName] &&
                formik.errors[option.fieldName]) ||
              (saveError?.status === 422 &&
                saveError?.response?.data?.errors?.[option.fieldName]?.join(
                  " "
                ))
            }
          />
        </Col>
      </Row>
    </>
  ));

  const statusPerLanguage = showMetaData?.data?.status_per_language;
  const defaultLanguage = showMetaData?.data?.default_language;
  const content = getTextFromHtml(defaultLanguage?.content);
  const dlContentCharCount = countChar(content);
  const dlContentWordCount = countWord(content);

  // Meta data
  const Meta = () => {
    const meta = showData?.data?.meta;

    const currentState = meta?.history;
    const lastOverwrite = meta?.last_overwrite;
    const lastTranslation = meta?.last_translation;

    const currentCharCount = currentCount?.char || 0;
    const currentWordCount = currentCount?.word || 0;

    return (
      <Row className="flex items-end border-b-2 border-gray-100 pb-2">
        <Col md={12}>
          <div className="text-jersium-black font-extralight text-sm grid grid-cols-2">
            <div>
              {/* Current state action */}
              {currentState &&
                (targetLang === DEFAULT_LANGUAGE ? (
                  <div className="text-red-700">
                    Original, used for Translation
                  </div>
                ) : (
                  <div>
                    {blogPostHistoryActionLabels[currentState.action] ?? null}{" "}
                    by {currentState.by}
                  </div>
                ))}

              {/* Last overwrite */}
              <div>
                {targetLang === DEFAULT_LANGUAGE
                  ? "Last Change: "
                  : "Last Overwrite: "}

                {lastOverwrite
                  ? `${formatDateTime(lastOverwrite.date)} by ${
                      lastOverwrite.by
                    }`
                  : "-"}
              </div>

              {/* Last translation */}
              <div>
                {targetLang !== DEFAULT_LANGUAGE &&
                  `Last Translation: ${
                    lastTranslation ? formatDateTime(lastTranslation.date) : "-"
                  }`}
              </div>
            </div>

            <div className="pl-3">
              {/* Counts */}
              <div>
                Character Count ({DEFAULT_LANGUAGE_LABEL} / Current):{" "}
                {!showMetaData || showMetaIsFetching ? "-" : dlContentCharCount}{" "}
                / {currentCharCount}
              </div>
              <div>
                Word Count ({DEFAULT_LANGUAGE_LABEL} / Current):{" "}
                {!showMetaData || showMetaIsFetching ? "-" : dlContentWordCount}{" "}
                / {currentWordCount}
              </div>

              {/* # times translated */}
              <div>
                {targetLang !== DEFAULT_LANGUAGE &&
                  `# Times Translated: ${
                    !meta || showIsFetching
                      ? "-"
                      : currentState.translated_count
                  }`}
              </div>
            </div>
          </div>
        </Col>
      </Row>
    );
  };

  // Default language forms
  const defaultLangForms = targetLang === DEFAULT_LANGUAGE && (
    <>
      <Row>
        <Col md={6}>
          <BasicInput
            formik={formik}
            fieldName={"code"}
            saveError={saveError}
            label="ID"
            labelClass="mb-0 flex items-center"
            inputDivClass={"grid grid-cols-6"}
            inputClass={"col-span-5"}
            errorTextClass={"block"}
          />
        </Col>
        <Col md={6}>
          <BasicInput
            formik={formik}
            fieldName={"tagline"}
            saveError={saveError}
            label="Sub Title"
            labelClass="mb-0 flex items-center"
            inputDivClass={"grid grid-cols-6"}
            inputClass={"col-span-5"}
            errorTextClass={"block"}
          />
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <BasicInput
            formik={formik}
            fieldName={`name`}
            saveError={saveError}
            label={`Team Name (${targetLang.toUpperCase()})`}
            labelClass="mb-0 flex items-center"
            inputDivClass={"grid grid-cols-6"}
            inputClass={"col-span-5"}
            errorTextClass={"block"}
          />
        </Col>

        <Col md={6}>
          <CreatableDropdown
            required={false}
            label="Tags"
            name="tags"
            labelClass="mb-0 flex items-center"
            containerClassName="!grid-cols-6 !gap-0"
            dropdownWrapperClassName="!col-span-5 !ml-0"
            placeholder="Type Team Name (Tags)..."
            isMulti
            value={formik.values.tags}
            onChange={(selected) => formik.setFieldValue("tags", selected)}
            errorText={formik.errors.tags}
          />
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <AsyncDropdown
            label="Team Group"
            placeholder="No team group selected. Type to search..."
            labelClass="mb-0 flex items-center"
            containerClassName="!grid-cols-6 !gap-0"
            dropdownWrapperClassName="!col-span-5 !ml-0"
            defaultOptions={teamGroupDefaultOptions}
            loadOptions={teamGroupFetchOptions}
            isLoading={teamGroupLoading}
            value={teamGroupGetSelectedOption()}
            onChange={(selected) => {
              formik.setFieldValue("team_group_id", selected?.value || null);
            }}
            errorText={
              (formik.touched.team_group_id && formik.errors.team_group_id) ||
              (saveError?.status === 422 &&
                saveError?.response?.data?.errors?.team_group_id?.join(" "))
            }
          />
        </Col>
        <Col md={6}>
          <AsyncDropdown
            height={32}
            label="Sport"
            labelClass="mb-0 flex items-center"
            containerClassName="!grid-cols-6 !gap-0"
            dropdownWrapperClassName="!col-span-5 !ml-0"
            placeholder="No sport selected. Type to search..."
            defaultOptions={sportDefaultOptions}
            loadOptions={sportFetchOptions}
            isLoading={sportLoading}
            value={sportGetSelectedOption()}
            onChange={(selected) => {
              formik.setFieldValue("sport_id", selected?.value || null);
            }}
            errorText={
              (formik.touched.sport_id && formik.errors.sport_id) ||
              (saveError?.status === 422 &&
                saveError?.response?.data?.errors?.sport_id?.join(" "))
            }
          />
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <CreatableDropdown
            required={false}
            isAsync
            label="City"
            name="city"
            className="mb-4"
            placeholder="No city selected. Type to search..."
            labelClass="mb-0 flex items-center"
            containerClassName="!grid-cols-6 !gap-0"
            dropdownWrapperClassName="!col-span-5 !ml-0"
            defaultOptions={cityDefaultOptions}
            loadOptions={cityFetchOptions}
            isLoading={cityLoading}
            value={cityGetSelectedOption()}
            onChange={(selected) => {
              console.log(cityGetSelectedOption());
              formik.setFieldValue(
                "city",
                selected?.value || formik.values.city || null
              );
            }}
            errorText={
              (formik.touched.city && formik.errors.city) ||
              (saveError?.status === 422 &&
                saveError?.response?.data?.errors?.city?.join(" "))
            }
          />
        </Col>

        <Col md={6}>
          <BasicInput
            required={false}
            type="color"
            formik={formik}
            fieldName={"hexcode_1"}
            saveError={saveError}
            label="Hexcode 1"
            labelClass="mb-0 flex items-center"
            inputDivClass={"grid grid-cols-6"}
            inputClass={"col-span-5"}
            errorTextClass={"block"}
          />
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <AsyncDropdown
            required={false}
            label="Country"
            placeholder="No country selected. Type to search..."
            labelClass="mb-0 flex items-center"
            containerClassName="!grid-cols-6 !gap-0"
            dropdownWrapperClassName="!col-span-5 !ml-0"
            defaultOptions={countryDefaultOptions}
            loadOptions={countryFetchOptions}
            isLoading={countryLoading}
            value={countryGetSelectedOption()}
            onChange={(selected) => {
              formik.setFieldValue(
                "country_id",
                selected?.value || formik.values.country_id || null
              );
            }}
            errorText={
              (formik.touched.country_id && formik.errors.country_id) ||
              (saveError?.status === 422 &&
                saveError?.response?.data?.errors?.country_id?.join(" "))
            }
          />
        </Col>

        <Col md={6}>
          <BasicInput
            required={false}
            type="color"
            formik={formik}
            fieldName={"hexcode_2"}
            saveError={saveError}
            label="Hexcode 2"
            labelClass="mb-0 flex items-center"
            inputDivClass={"grid grid-cols-6"}
            inputClass={"col-span-5"}
            errorTextClass={"block"}
          />
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <BasicInput
            required={false}
            type="number"
            formik={formik}
            fieldName={"foundation_year"}
            saveError={saveError}
            label="Foundation Year"
            labelClass="mb-0 flex items-center"
            inputDivClass={"grid grid-cols-6"}
            inputClass={"col-span-5"}
            errorTextClass={"block"}
          />
        </Col>
        <Col md={6}>
          <BasicInput
            required={false}
            type="color"
            formik={formik}
            fieldName={"hexcode_3"}
            saveError={saveError}
            label="Hexcode 3"
            labelClass="mb-0 flex items-center"
            inputDivClass={"grid grid-cols-6"}
            inputClass={"col-span-5"}
            errorTextClass={"block"}
          />
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <BasicInput
            required={false}
            type="number"
            formik={formik}
            fieldName={"field_width"}
            saveError={saveError}
            label="Field Width"
            labelClass="mb-0 flex items-center"
            inputDivClass={"grid grid-cols-6"}
            inputClass={"col-span-5"}
            errorTextClass={"block"}
          />
        </Col>
        <Col md={6}>
          <BasicInput
            required={false}
            type="color"
            formik={formik}
            fieldName={"hexcode_4"}
            saveError={saveError}
            label="Hexcode 4"
            labelClass="mb-0 flex items-center"
            inputDivClass={"grid grid-cols-6"}
            inputClass={"col-span-5"}
            errorTextClass={"block"}
          />
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <BasicInput
            required={false}
            type="number"
            formik={formik}
            fieldName={"field_length"}
            saveError={saveError}
            label="Field Length"
            labelClass="mb-0 flex items-center"
            inputDivClass={"grid grid-cols-6"}
            inputClass={"col-span-5"}
            errorTextClass={"block"}
          />
        </Col>
        <Col md={6}>
          <BasicInput
            required={false}
            formik={formik}
            fieldName={"mascot_1"}
            saveError={saveError}
            label="Mascot 1"
            labelClass="mb-0 flex items-center"
            inputDivClass={"grid grid-cols-6"}
            inputClass={"col-span-5"}
            errorTextClass={"block"}
          />
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <BasicInput
            required={false}
            type="number"
            formik={formik}
            fieldName={"stadium_capacity"}
            saveError={saveError}
            label="Stadium Capacity"
            labelClass="mb-0 flex items-center"
            inputDivClass={"grid grid-cols-6"}
            inputClass={"col-span-5"}
            errorTextClass={"block"}
          />
        </Col>
        <Col md={6}>
          <BasicInput
            required={false}
            formik={formik}
            fieldName={"mascot_2"}
            saveError={saveError}
            label="Mascot 2"
            labelClass="mb-0 flex items-center"
            inputDivClass={"grid grid-cols-6"}
            inputClass={"col-span-5"}
            errorTextClass={"block"}
          />
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <BasicInput
            required={false}
            formik={formik}
            fieldName={"stadium_name"}
            saveError={saveError}
            label="Stadium Name"
            labelClass="mb-0 flex items-center"
            inputDivClass={"grid grid-cols-6"}
            inputClass={"col-span-5"}
            errorTextClass={"block"}
          />
        </Col>
        <Col md={6}>
          <BasicInput
            formik={formik}
            required={false}
            fieldName={"anthem_name"}
            saveError={saveError}
            label="Anthem Name"
            labelClass="mb-0 flex items-center"
            inputDivClass={"grid grid-cols-6"}
            inputClass={"col-span-5"}
            errorTextClass={"block"}
          />
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <RadioInput
            options={booleanOptionFormOptionsMap}
            formik={formik}
            saveError={saveError}
            fieldName={"show_in_profile"}
            label={"Show Profile"}
          />
        </Col>
        <Col md={6}>
          <StatusInput
            formik={formik}
            saveError={saveError}
            label="MPL Status"
          />
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <RadioInput
            options={booleanOptionFormOptionsMap}
            formik={formik}
            saveError={saveError}
            fieldName={"is_national_team"}
            label={"National Team"}
          />
        </Col>
      </Row>

      {teamRivalInputs}

      <Row>
        <Col md={6}>
          <ImageInput
            id="icon_url"
            formFormik={formik}
            fieldName="icon_url"
            label="Icon"
            labelClass="mb-0 flex items-center"
            error={saveError}
            existingImageUrl={isEdit && showData?.data?.icon_url}
            clickablePreview={true}
            customStyle={{ width: "100%" }}
            imagePreviewWrapper={"col-span-5"}
            className={"grid grid-cols-6"}
            inputContainerClass={"col-span-5 border-0"}
            imagePreviewContainer={"grid grid-cols-6 gap-3"}
            errorTextClass={"ml-0"}
          />
        </Col>
        <Col md={6}>
          <RadioInput
            options={booleanOptionFormOptionsMap}
            formik={formik}
            saveError={saveError}
            fieldName={"is_icon_bordered"}
            label={"Icon Bordered"}
          />
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <ImageInput
            id="banner_url"
            formFormik={formik}
            fieldName="banner_url"
            label="Banner"
            labelClass="mb-0 flex items-center"
            error={saveError}
            existingImageUrl={isEdit && showData?.data?.banner_url}
            clickablePreview={true}
            customStyle={{ width: "100%" }}
            imagePreviewWrapper={"col-span-5"}
            className={"grid grid-cols-6"}
            inputContainerClass={"col-span-5 border-0"}
            imagePreviewContainer={"grid grid-cols-6 gap-3"}
            errorTextClass={"ml-0"}
          />
        </Col>
        <Col md={6}>
          <ImageInput
            id="thumbnail_url"
            formFormik={formik}
            fieldName="thumbnail_url"
            label="Thumbnail"
            labelClass="mb-0 flex items-center"
            error={saveError}
            existingImageUrl={isEdit && showData?.data?.thumbnail_url}
            clickablePreview={true}
            customStyle={{ width: "100%" }}
            imagePreviewWrapper={"col-span-5"}
            className={"grid grid-cols-6"}
            inputContainerClass={"col-span-5 border-0"}
            imagePreviewContainer={"grid grid-cols-6 gap-3"}
            errorTextClass={"ml-0"}
          />
        </Col>
      </Row>
    </>
  );

  const isPending = saveIsPending || saveTranslationIsPending;
  const error = saveError || saveTranslationError;

  // Error handlers
  useFormikErrorToast(formik);
  useHttpErrorToast(error);

  return (
    <>
      <div className="-mt-1.5">
        <PageHeaderName
          title={isEdit ? "Edit Team Profile" : "Add Team Profile"}
        />
      </div>

      <ul className="flex flex-grow text-sm font-medium text-center text-white border-b-2 border-gray-100 mt-2 mb-2.5 w-full">
        {locales.map((locale) => (
          <li key={locale.code}>
            <button
              type="button"
              aria-current="page"
              className={cn(
                "flex gap-x-2 items-center px-3 py-2 rounded-t-lg text-nowrap disabled:cursor-not-allowed",
                targetLang === locale.code
                  ? "text-jersium-black bg-gray-100"
                  : "text-gray-500 disabled:text-gray-300 enabled:hover:bg-gray-100"
              )}
              onClick={(e) => handleLanguageTab(e, locale.code)}
              disabled={!id && locale.code !== DEFAULT_LANGUAGE}
            >
              {/* Team status circle indicator */}
              {!isEdit && locale.code !== DEFAULT_LANGUAGE ? (
                <span className="inline-block w-2 h-2 rounded-full bg-gray-100" />
              ) : (
                <span
                  className="inline-block w-2 h-2 rounded-full"
                  style={{
                    background:
                      statusBgColors[
                        statusPerLanguage?.[locale.code] || STATUS.INACTIVE
                      ],
                  }}
                />
              )}

              {/* The locale */}
              <span>
                {locale.code.toUpperCase()}
                {targetLang === locale.code && formik.dirty ? "*" : " "}
              </span>

              {/* Loading spinner */}
              {targetLang === locale.code && showIsFetching && (
                <Spinner color="gray" />
              )}
            </button>
          </li>
        ))}
      </ul>

      {!(isEdit && showIsFetching) && (
        <form
          onSubmit={formik ? formik.handleSubmit : null}
          className="overflow-hidden flex gap-y-2 flex-col justify-center items-center w-full"
        >
          <div className="flex-1 flex flex-col gap-y-2 w-full">
            <Meta />
            <Row className="border-b-2 border-gray-100 pb-2 mb-2">
              <Col md={12}>
                <div className="mt-2 w-full flex justify-between">
                  <div className="gap-x-6 flex justify-start items-center">
                    <label className="block text-sm me-4">
                      Language Status
                    </label>
                    <div className="flex gap-x-2 items-center">
                      {statusRadioOptions.map((option) => (
                        <label key={option.value} className="flex gap-2">
                          <input
                            id={`status-${option.value}`}
                            type="radio"
                            value={option.value}
                            checked={
                              formik.values.history_status === option.value
                            }
                            onChange={() =>
                              formik.setFieldValue(
                                "history_status",
                                option.value
                              )
                            }
                          />
                          <div
                            className={cn(
                              "flex items-center gap-2 px-3 py-1 h-8 rounded-md",
                              option.value === STATUS.ACTIVE
                                ? "active-status"
                                : "deActive-status"
                            )}
                          >
                            <span className="text-sm font-extralight">
                              {option.label}
                            </span>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="gap-x-6 flex justify-start">
                    <div className="flex justify-end items-center gap-2.5 h-9">
                      {targetLang !== DEFAULT_LANGUAGE ? (
                        <Button
                          variant="green-gradient"
                          label={`${
                            isPending ? LOADING_PLACEHOLDER : "Translate"
                          }`}
                          onClick={handleTranslate}
                          type="button"
                          disabled={isPending}
                          className="!w-[200px] !text-[14px]"
                        />
                      ) : (
                        <>
                          <div className="flex my-auto">
                            <input
                              id="auto_translate"
                              type="checkbox"
                              value={formik.values.auto_translate}
                              checked={formik.values.auto_translate}
                              className={`h-3 w-3 border rounded-md ${
                                formik.touched.auto_translate &&
                                formik.errors.auto_translate
                                  ? "border-red-500 bg-red-50"
                                  : "border-gray-300"
                              }`}
                              onChange={(event) =>
                                formik.setFieldValue(
                                  "auto_translate",
                                  event.target.checked
                                )
                              }
                            />
                          </div>
                          <label
                            htmlFor="auto_translate"
                            className="block text-sm"
                          >
                            Translate All
                          </label>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="gap-x-6 flex justify-start">
                    <div>
                      <Button
                        variant="outlined"
                        label="Cancel/Back"
                        onClick={() =>
                          navigate("/web-data-management/master-data/teams")
                        }
                        type="button"
                        disabled={isPending}
                        className="!w-[200px] !text-[14px]"
                      />
                    </div>
                    <div>
                      <Button
                        variant="green-gradient"
                        label={`${isPending ? LOADING_PLACEHOLDER : "Save"}`}
                        type="submit"
                        disabled={isPending}
                        className="!w-[200px] !text-[14px]"
                      />
                    </div>
                  </div>
                </div>
              </Col>
            </Row>

            {targetLang !== DEFAULT_LANGUAGE && (
              <Row>
                <Col md={6}>
                  <BasicInput
                    formik={formik}
                    fieldName={`name`}
                    saveError={saveError}
                    label={`Team Name (${targetLang.toUpperCase()})`}
                    labelClass="mb-0 flex items-center"
                    inputDivClass={"grid grid-cols-6"}
                    inputClass={"col-span-5"}
                    errorTextClass={"block"}
                  />
                </Col>
                <Col md={6}>
                  <BasicInput
                    formik={formik}
                    fieldName={"tagline"}
                    saveError={saveError}
                    label="Sub Title"
                    labelClass="mb-0 flex items-center"
                    inputDivClass={"grid grid-cols-6"}
                    inputClass={"col-span-5"}
                    errorTextClass={"block"}
                  />
                </Col>
              </Row>
            )}

            {targetLang === DEFAULT_LANGUAGE && defaultLangForms}
            <Row>
              <Col md={12}>
                <RichTextEditor
                  formik={formik}
                  fieldName={"content"}
                  saveError={saveError}
                  label={""}
                  enableCharCounter={false}
                  enableWordCounter={false}
                  onChange={(editor) => {
                    const editorText = editor.getText();
                    updateCounts(editorText);
                    const newContent = editor.getHTML();
                    formik.setFieldValue("content", newContent);
                  }}
                />
              </Col>
            </Row>
          </div>
        </form>
      )}
    </>
  );
}
